import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Sparkles, Plus, Save, Trash2, Eye, EyeOff, Image } from "lucide-react";

const CATS = ["apparel", "drinkware", "goods", "jewelry"];
const inputCls = "w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";

export const GoodiesManager = () => {
  const { t } = useLang();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", price_chf: "", stock: "", category: "apparel", image: "", description: "", featured: false, printful_variant_id: "", printful_design_url: "" });

  const { data: products = [] } = useQuery({ queryKey: ["dsProducts"], queryFn: async () => (await api.get("/admin/products")).data });
  const goodies = products.filter((p) => !p.dropship && p.edition === "swiss-pagan");

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["dsProducts"] }); qc.invalidateQueries({ queryKey: ["products"] }); };

  const addGoodie = async () => {
    const price = parseFloat(form.price_chf);
    if (!form.name || isNaN(price) || price < 0) { toast.error(t("gd_price_chf")); return; }
    setBusy(true);
    try {
      await api.post("/admin/products", {
        name: form.name, description: form.description, category: form.category, image: form.image,
        featured: form.featured, dropship: false, price_chf: price, stock: parseInt(form.stock || "0", 10), edition: "swiss-pagan",
        printful_variant_id: form.printful_variant_id ? parseInt(form.printful_variant_id, 10) : null,
        printful_design_url: form.printful_design_url || null, pod: !!form.printful_variant_id,
      });
      toast.success(t("gd_created"));
      setForm({ name: "", price_chf: "", stock: "", category: "apparel", image: "", description: "", featured: false, printful_variant_id: "", printful_design_url: "" });
      setAdding(false);
      invalidate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  return (
    <div data-testid="goodies-manager">
      <h2 className="font-serif text-2xl text-stone-100 mt-14 mb-2 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> {t("gd_title")}</h2>
      <p className="text-sm text-stone-400 mb-6 max-w-2xl">{t("gd_desc")}</p>

      <div className="mb-6">
        {!adding ? (
          <button data-testid="gd-add-toggle" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 bg-[#181A20] border border-[#272A34] hover:border-amber-500/40 text-stone-200 px-4 py-2 rounded-md text-sm"><Plus className="w-4 h-4 text-amber-500" /> {t("gd_add")}</button>
        ) : (
          <div className="bg-[#121418] border border-amber-800/40 rounded-lg p-5 grid sm:grid-cols-2 gap-3" data-testid="gd-add-form">
            <input data-testid="gd-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("ds_name")} className={inputCls} />
            <input data-testid="gd-new-price" type="number" value={form.price_chf} onChange={(e) => setForm({ ...form, price_chf: e.target.value })} placeholder={t("gd_price_chf")} className={inputCls} />
            <input data-testid="gd-new-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder={t("gd_stock")} className={inputCls} />
            <select data-testid="gd-new-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <input data-testid="gd-new-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder={t("ds_image_url")} className={`${inputCls} sm:col-span-2`} />
            <input data-testid="gd-new-variant" value={form.printful_variant_id} onChange={(e) => setForm({ ...form, printful_variant_id: e.target.value })} placeholder={t("gd_variant")} className={inputCls} />
            <input data-testid="gd-new-design" value={form.printful_design_url} onChange={(e) => setForm({ ...form, printful_design_url: e.target.value })} placeholder={t("gd_design")} className={inputCls} />
            <input data-testid="gd-new-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("gd_desc_ph")} className={`${inputCls} sm:col-span-2`} />
            <label className="flex items-center gap-2 text-sm text-stone-300"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-amber-600" /> {t("ds_featured")}</label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAdding(false)} className="px-4 py-2 rounded-md text-sm text-stone-400 hover:text-stone-200">✕</button>
              <button data-testid="gd-new-submit" onClick={addGoodie} disabled={busy} className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-60"><Plus className="w-4 h-4" /> {t("gd_add")}</button>
            </div>
          </div>
        )}
      </div>

      <h3 className="font-mono text-xs uppercase tracking-wider text-stone-500 mb-3">{t("gd_list")} ({goodies.length})</h3>
      {goodies.length === 0 ? (
        <div className="text-center py-10 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="gd-none">{t("gd_none")}</div>
      ) : (
        <div className="space-y-3" data-testid="gd-product-list">
          {goodies.map((p) => <GoodieRow key={p.slug} product={p} onChange={invalidate} />)}
        </div>
      )}
    </div>
  );
};

const GoodieRow = ({ product, onChange }) => {
  const { t } = useLang();
  const [chf, setChf] = useState((product.price_cents / 100).toFixed(2));
  const [stock, setStock] = useState(product.stock ?? 0);
  const [featured, setFeatured] = useState(!!product.featured);
  const [active, setActive] = useState(product.active !== false);
  const [variant, setVariant] = useState(product.printful_variant_id || "");
  const [busy, setBusy] = useState(false);

  const put = async (payload, msg) => {
    setBusy(true);
    try { await api.put(`/admin/products/${product.slug}`, payload); if (msg) toast.success(msg); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const toggleActive = async () => { const next = !active; setActive(next); await put({ active: next }, next ? t("product_visible") : t("product_hidden")); };
  const save = () => put({ price_chf: parseFloat(chf), stock: parseInt(stock || "0", 10), featured, printful_variant_id: variant ? parseInt(variant, 10) : null, pod: !!variant }, t("ds_updated"));
  const genMockups = async () => {
    setBusy(true);
    try { await api.post(`/admin/printful/mockups/${product.slug}`); toast.success(t("gd_mockups_done")); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  const del = async () => {
    if (!window.confirm(`${t("ds_delete")} — ${product.name}?`)) return;
    setBusy(true);
    try { await api.delete(`/admin/products/${product.slug}`); toast.success(t("ds_deleted")); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  return (
    <div className={`flex flex-col lg:flex-row lg:items-center gap-3 bg-[#121418] border rounded-lg p-3 ${active ? "border-[#272A34]" : "border-amber-800/40 opacity-70"}`} data-testid={`gd-row-${product.slug}`}>
      {product.image && <img src={product.image} alt="" className="w-14 h-14 rounded-md object-cover shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="font-serif text-stone-100 text-sm truncate">{product.name}</div>
        <div className="text-[11px] font-mono text-amber-500/70 truncate uppercase tracking-wider">{t("store_edition_badge")}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid={`gd-toggle-${product.slug}`} onClick={toggleActive} disabled={busy} title={active ? t("product_hide") : t("product_show")} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs ${active ? "bg-[#181A20] border border-[#272A34] text-stone-300 hover:border-amber-500/40" : "bg-amber-900/40 border border-amber-700/40 text-amber-300"}`}>{active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>
        <div className="flex items-center gap-1">
          <span className="text-[11px] font-mono text-stone-500">CHF</span>
          <input data-testid={`gd-price-${product.slug}`} type="number" value={chf} onChange={(e) => setChf(e.target.value)} className="w-24 bg-[#181A20] border border-[#272A34] rounded-md px-2 py-1.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] font-mono text-stone-500">{t("gd_stock")}</span>
          <input data-testid={`gd-stock-${product.slug}`} type="number" value={stock} onChange={(e) => setStock(e.target.value)} className="w-20 bg-[#181A20] border border-[#272A34] rounded-md px-2 py-1.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-stone-400"><input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} className="accent-amber-600" /> ★</label>
        <div className="flex items-center gap-1" title={t("gd_variant")}>
          <span className="text-[11px] font-mono text-stone-500">PF</span>
          <input data-testid={`gd-variant-${product.slug}`} type="number" value={variant} onChange={(e) => setVariant(e.target.value)} placeholder="ID" className="w-20 bg-[#181A20] border border-[#272A34] rounded-md px-2 py-1.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
        </div>
        <button data-testid={`gd-save-${product.slug}`} onClick={save} disabled={busy} className="inline-flex items-center gap-1 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-3 py-1.5 rounded-md text-xs disabled:opacity-60"><Save className="w-3.5 h-3.5" /> {t("ds_save")}</button>
        {(product.variants?.length > 0 || product.printful_variant_id) && (
          <button data-testid={`gd-mockups-${product.slug}`} onClick={genMockups} disabled={busy} title={t("gd_mockups")} className="inline-flex items-center gap-1 bg-indigo-900/60 hover:bg-indigo-800 text-indigo-100 px-3 py-1.5 rounded-md text-xs disabled:opacity-60 border border-indigo-700/40"><Image className="w-3.5 h-3.5" /></button>
        )}
        <button data-testid={`gd-delete-${product.slug}`} onClick={del} disabled={busy} className="inline-flex items-center gap-1 bg-red-900/70 hover:bg-red-800 text-white px-3 py-1.5 rounded-md text-xs disabled:opacity-60"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};

export default GoodiesManager;
